import { useState } from "react";
import { useLingui } from "@lingui/react";
import { Soup, ChefHat, Carrot, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";

// Menu items are created from the Menus page, so they are not offered here.
type RecipeType = "platedRecipe" | "prepRecipe" | "ingredient";

interface CreateNewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (type: RecipeType) => void;
}

const tiles: {
  type: RecipeType;
  icon: typeof Soup;
}[] = [
  {
    type: "platedRecipe",
    icon: Soup,
  },
  {
    type: "prepRecipe",
    icon: ChefHat,
  },
  {
    type: "ingredient",
    icon: Carrot,
  },
];

export function CreateNewDialog({
  open,
  onOpenChange,
  onSelect,
}: CreateNewDialogProps) {
  const { i18n } = useLingui();
  const [hovered, setHovered] = useState<RecipeType | null>(null);

  const getTileLabel = (type: RecipeType) => {
    if (type === "platedRecipe") return i18n._("Add plated recipe");
    if (type === "prepRecipe") return i18n._("Add prep recipe");
    return i18n._("Add ingredient");
  };

  const getTileDescription = (type: RecipeType) => {
    if (type === "platedRecipe") return i18n._("A dish you serve to a guest. Costed per plate, and can be sold as a menu item.");
    if (type === "prepRecipe") return i18n._("A make-ahead recipe used as a component in menu items.");
    return i18n._("A single ingredient or raw material used in recipes.");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="max-w-[800px] p-0 gap-0 overflow-hidden border border-(--shell-border) rounded-[12px]"
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5">
          <div className="flex flex-col gap-2">
            <DialogTitle className="text-2xl font-medium text-(--color-text-primary) leading-6">
              {i18n._("Create new")}
            </DialogTitle>
            <p className="text-base text-(--color-text-secondary) font-normal leading-none">
              {i18n._("Choose what you want to create.")}
            </p>
          </div>
          <DialogClose
            aria-label={i18n._("Close")}
            className="text-(--color-text-secondary) hover:text-(--color-text-primary) transition-colors shrink-0"
          >
            <X className="size-6" />
          </DialogClose>
        </div>

        {/* Tiles */}
        <div className="grid grid-cols-1 gap-3 px-5 pb-5 sm:grid-cols-3">
          {tiles.map(({ type, icon: Icon }) => {
            const isActive = hovered === type;
            return (
              <Button
                key={type}
                variant="ghost"
                onMouseEnter={() => setHovered(type)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => {
                  onOpenChange(false);
                  onSelect(type);
                }}
                className={`h-auto flex-col items-center justify-center gap-4 rounded-[12px] p-4 whitespace-normal ${
                  isActive
                    ? "bg-(--color-surface-accent) border border-(--color-border-strong) hover:bg-(--color-surface-accent)"
                    : "bg-(--color-surface-muted) border border-(--color-border) hover:bg-(--color-surface-accent)"
                }`}
              >
                <div className="flex h-12 items-center rounded-[8px] border border-(--color-border) bg-(--color-surface-content) px-3">
                  <Icon className="size-6 text-(--color-text-primary)" />
                </div>
                <div className="flex flex-col items-center gap-2 text-center">
                  <span className="text-base font-medium text-(--color-text-primary) leading-tight whitespace-normal break-words">
                    {getTileLabel(type)}
                  </span>
                  <span className="text-sm text-(--color-text-secondary) leading-snug whitespace-normal break-words">
                    {getTileDescription(type)}
                  </span>
                </div>
              </Button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
